import React, { useMemo, useState, useEffect } from "react"
import { User } from "../../models/user.model";
import RemoveUserModal from "./modals/RemoveUserModal";
import ModifyUserModal from "./modals/ModifyUserModal";
import { responseHero } from "../../utils/axiosInterceptor";
import { Spinner } from "../../utils/Spinner";
import { useLocation, useNavigate } from "react-router";
import { Group } from "../../models/group.model";
import { toast } from "react-toastify";
import { Template } from "../../models/templates.model";
import { SET_SELECTED_USER } from "../../store/reducers/selectedUserSlice";
import { useDispatch } from "react-redux";
import CreateUserModal from "./modals/CreateUserModal";
import { Organization } from "../../models/organization.model";
import { REMOVE_SELECTED_ORG } from "../../store/reducers/selectedOrgSlice";
import GoToUserProfile from "./modals/GoToUserProfile";
import UserInvestmentsModal from "./modals/UserInvestmentsModal";
import PasswordHelper from "./modals/PasswordHelper";

type UserListProps = {
    searchText: string;
    searchOption: string;
    selectedProject: number;
};

type SortConfig = {
    key: keyof User;
    direction: 'asc' | 'desc';
} | null;

export default function UserList({ searchText, searchOption, selectedProject }: UserListProps) {
    const [originalUsers, setOriginalUsers] = useState<User[]>([]);
    const [spinner, setSpinner] = useState<boolean>(true);
    const [stateChange, setStateChange] = useState<boolean>(true);
    const [sortConfig, setSortConfig] = useState<SortConfig>(null);
    const [selectedUser, setSelectedUser] = useState<User>();

    // Modal states
    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const [showModifyModal, setShowModifyModal] = useState<boolean>(false);
    const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
    const [showStalkerModal, setShowStalkerModal] = useState<boolean>(false);
    const [showPasswordModal, setShowPasswordModal] = useState<boolean>(false);
    const [showInvModal, setShowInvModal] = useState<boolean>(false);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { state } = useLocation();

    // Context from router state
    const { group, template, org }: { group?: Group, template?: Template, org?: Organization } = state || {};

    const getUsers = async () => {
        setSpinner(true);
        try {
            const response = await responseHero.get(`${process.env.REACT_APP_API_URL}/users/`);
            setOriginalUsers(response.data);
        } catch (error) {
            console.error(error);
            toast.error("Hiba a felhasználók betöltésekor");
        }
        setSpinner(false);
    };

    useEffect(() => {
        getUsers();
    }, [selectedProject, stateChange]);

    // Data Processing: Filter & Sort
    const processedUsers = useMemo(() => {
        let items = [...originalUsers];

        if (searchText) {
            const searchValue = searchText.toLowerCase();
            items = items.filter((user) => {
                if (searchOption === 'name') return user.name.toLowerCase().includes(searchValue);
                if (searchOption === 'email') return user.email.toLowerCase().includes(searchValue);
                if (searchOption === 'phone') return user.phone?.includes(searchValue);
                return true;
            });
        }

        if (sortConfig) {
            items.sort((a, b) => {
                const aVal = a[sortConfig.key] ?? '';
                const bVal = b[sortConfig.key] ?? '';
                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return items;
    }, [originalUsers, searchText, searchOption, sortConfig]);

    const requestSort = (key: keyof User) => {
        let direction: 'asc' | 'desc' = 'asc';
        if (sortConfig?.key === key && sortConfig.direction === 'asc') direction = 'desc';
        setSortConfig({ key, direction });
    };

    const formatLastLogin = (dateVal: any) => {
        const d = String(dateVal);
        if (!dateVal || d.startsWith('1900-01-01')) return 'N/A (Sosem lépett be)';
        return d.split('T')[0];
    };

    /* Actions */
    const addMemberToGroup = async (user: User) => {
        if (!group) return;
        await responseHero.post(`${process.env.REACT_APP_API_URL}/addUserToGroup`, { group_id: group.group_id, user_id: user.user_id })
            .then(() => toast.success(`${user.name} hozzáadva a csoporthoz.`))
            .catch(() => toast.error('Hiba! Talán már tag?'));
    };

    const addMemberToContract = (user: User) => {
        dispatch(SET_SELECTED_USER(user));
        dispatch(REMOVE_SELECTED_ORG());
        navigate('/contracts');
    };

    const addMemberToOrganization = async (user: User) => {
        if (!org) return;
        await responseHero.put(`${process.env.REACT_APP_API_URL}/leader/${org.id}`, { user_id: user.user_id })
            .then(() => {
                toast.success(`${user.name} hozzárendelve.`);
                navigate('/orgmanager');
            })
            .catch(() => toast.error('Hiba a hozzárendelés során!'));
    };

    return (
        <>
            <RemoveUserModal title="Felhasználó törlése" user={selectedUser} showModal={showRemoveModal} closeModal={() => setShowRemoveModal(false)} handleStateChange={() => setStateChange(!stateChange)} />
            <ModifyUserModal title="adatai" user={selectedUser} showModal={showModifyModal} closeModal={() => setShowModifyModal(false)} handleStateChange={() => setStateChange(!stateChange)} />
            <CreateUserModal title="Új ügyfél" user={selectedUser} showModal={showCreateModal} closeModal={() => setShowCreateModal(false)} handleStateChange={() => setStateChange(!stateChange)} />
            <GoToUserProfile title="Átirányítás" user={selectedUser} showModal={showStalkerModal} closeModal={() => setShowStalkerModal(false)} />
            <PasswordHelper title="Jelszó emlékeztető" user={selectedUser} showModal={showPasswordModal} closeModal={() => setShowPasswordModal(false)} />
            <UserInvestmentsModal user={selectedUser} title="Befektetés" showModal={showInvModal} closeModal={() => setShowInvModal(false)} />

            <div className="m-10">
                <Spinner show={spinner} width={12} height={12} />

                {/* Sorting Controls */}
                <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-4">
                    <button onClick={() => setShowCreateModal(true)} className="hover:opacity-80 rounded-lg shadow-lg bg-success text-sm text-white p-2 px-6">
                        Új ügyfél felvétele
                    </button>

                    <button onClick={() => navigate('/usermap', { state: { originalUsers } })} className="hover:opacity-80 rounded-lg shadow-lg bg-brown text-sm text-white p-2 px-6">
                        Ügyféltérkép megtekintése
                    </button>

                    <div className="flex gap-2 items-center bg-gray-100 p-2 rounded-lg border">
                        <span className="text-xs font-bold text-gray-500 uppercase px-2">Rendezés:</span>
                        {(['name', 'email', 'last_logged'] as const).map(key => (
                            <button 
                                key={key}
                                onClick={() => requestSort(key)} 
                                className={`px-3 py-1 rounded text-sm transition-all ${sortConfig?.key === key ? 'bg-dark-blue text-white' : 'bg-white text-gray-600 hover:bg-gray-200'}`}>
                                {key === 'name' ? 'Név' : key === 'email' ? 'Email' : 'Belépés'} 
                                {sortConfig?.key === key && (sortConfig.direction === 'asc' ? ' ↑' : ' ↓')}
                            </button>
                        ))}
                    </div>
                </div>

                <p className="text-gray-800 py-2 text-2xl text-center border-b-4 mb-6">
                    Ügyfelek száma: <span className="text-brown">{processedUsers.length} db</span>
                </p>

                {!spinner && (
                    <div className="flex justify-center border p-3 m-2 mx-auto overflow-x-auto shadow-sm">
                        <table className="min-w-full divide-y divide-gray-600 table-fixed">
                            <thead className="bg-dark-blue text-cream">
                                <tr>
                                    <th className="py-3 px-6 text-xs font-medium text-left uppercase">ID</th>
                                    <th onClick={() => requestSort('name')} className="py-3 px-6 text-xs font-medium text-left uppercase cursor-pointer hover:underline">Név</th>
                                    <th onClick={() => requestSort('email')} className="py-3 px-6 text-xs font-medium text-left uppercase cursor-pointer hover:underline">Email</th>
                                    <th className="py-3 px-6 text-xs font-medium text-left uppercase">Telefonszám</th>
                                    <th onClick={() => requestSort('last_logged')} className="py-3 px-6 text-xs font-medium text-left uppercase cursor-pointer hover:underline">Belépés</th>
                                    
                                    {(!group && !template && !org) ? (
                                        <>
                                            <th className="p-4" colSpan={5}><span className="sr-only">Műveletek</span></th>
                                        </>
                                    ) : (
                                        <th className="p-4 text-xs font-medium text-left uppercase">Kiválaszt</th>
                                    )}
                                </tr>
                            </thead>

                            <tbody className="divide-y bg-dark-blue divide-gray-600">
                                {processedUsers.map((user) => (
                                    <tr key={user.user_id} className={`hover:bg-light-blue transition-colors ${selectedUser?.user_id === user.user_id ? 'bg-light-blue' : 'bg-dark-blue'}`}>
                                        <td className="py-4 px-6 text-sm text-white">{user.user_id}</td>
                                        <td className="py-4 px-6 text-sm text-white font-bold">{user.name} {user.admin && <span className="text-orange-400 ml-1">[ADMIN]</span>}</td>
                                        <td className="py-4 px-6 text-sm text-white">{user.email}</td>
                                        <td className="py-4 px-6 text-sm text-white">
                                            {(!user.phone || user.phone === '0' || user.phone === '') ? 'Nincs megadva' : (user.phone.startsWith('+') ? user.phone : `+36${user.phone}`)}
                                        </td>
                                        <td className="py-4 px-6 text-sm text-white">{formatLastLogin(user.last_logged)}</td>

                                        {(!group && !template && !org) ? (
                                            <>
                                                <td className="py-4 px-2 text-sm text-right whitespace-nowrap"><button onClick={() => { setSelectedUser(user); setShowStalkerModal(true); }} className="text-indigo-300 hover:underline">Kimutatás</button></td>
                                                <td className="py-4 px-2 text-sm text-right whitespace-nowrap"><button onClick={() => navigate(`/acchandler/${user.user_id}`, { state: { user } })} className="text-green-400 hover:underline">Számlák</button></td>
                                                <td className="py-4 px-2 text-sm text-right whitespace-nowrap"><button onClick={() => { setSelectedUser(user); setShowPasswordModal(true); }} className="text-red-400 hover:underline">Jelszó</button></td>
                                                <td className="py-4 px-2 text-sm text-right whitespace-nowrap"><button onClick={() => { setSelectedUser(user); setShowModifyModal(true); }} className="text-cream hover:underline">Módosít</button></td>
                                                <td className="py-4 px-2 text-sm text-right whitespace-nowrap"><button onClick={() => { setSelectedUser(user); setShowRemoveModal(true); }} className="text-red-500 hover:underline px-2">Törlés</button></td>
                                            </>
                                        ) : (
                                            <td className="py-4 px-6 text-sm text-right whitespace-nowrap font-medium">
                                                {group && <button onClick={() => addMemberToGroup(user)} className="text-cream hover:underline">Hozzáadás: {group.group_name}</button>}
                                                {template && <button onClick={() => addMemberToContract(user)} className="text-cream hover:underline">Hozzárendel: {template.name}</button>}
                                                {org && <button onClick={() => addMemberToOrganization(user)} className="text-cream hover:underline">Hozzárendel: {org.name}</button>}
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </>
    );
}